import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

export const FEATURE_FLAGS_STORAGE_KEY = 'feature-flags'
export const FEATURE_FLAG_PARAM_PREFIX = 'dev_feature_'

export type FeatureFlagName = 'chat'

export type FeatureFlagValue = boolean | null

type UseFeatureFlagReturn = [FeatureFlagValue, (enable: boolean) => void]

const readFlagFromStorage = (name: FeatureFlagName): FeatureFlagValue => {
  try {
    const flags = JSON.parse(
      localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY) || '{}'
    )
    return name in flags ? !!flags[name] : null
  } catch {
    return null
  }
}

const writeFlagToStorage = (name: FeatureFlagName, enable: boolean) => {
  try {
    const flags = JSON.parse(
      localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY) || '{}'
    )
    flags[name] = enable
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags))
  } catch (error) {
    console.error('Error updating feature flag:', error)
  }
}

export function useFeatureFlag(name: FeatureFlagName): UseFeatureFlagReturn {
  const [searchParams] = useSearchParams()
  const [value, setValue] = useState<FeatureFlagValue>(() =>
    readFlagFromStorage(name)
  )

  useEffect(() => {
    const paramValue = searchParams.get(`${FEATURE_FLAG_PARAM_PREFIX}${name}`)
    if (paramValue === 'true' || paramValue === 'false') {
      const enable = paramValue === 'true'
      writeFlagToStorage(name, enable)
      setValue(enable)
    }
  }, [name, searchParams])

  const setFeatureFlag = (enable: boolean) => {
    writeFlagToStorage(name, enable)
    setValue(enable)
  }

  return [value, setFeatureFlag]
}

export default useFeatureFlag
